import { selectCurrentHotelName } from 'accessors'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import AddIcon from 'components/ReusableComponents/Icons/Add'
import InfoIcon from 'components/ReusableComponents/Icons/Info'
import Toggle from 'components/ReusableComponents/Toggle'
import { userRoleLevels } from 'features/UserPermissions'
import { useInternalAdmin } from 'hooks'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import userApi from 'services/api/user'

import styles from './InvoiceRecipientsDialog.module.scss'

const InvoiceRecipientsDialog = ({
  users: allUsers,
  onAddUserClick,
  onClose,
  onSave
}) => {
  const hotelName = useSelector(selectCurrentHotelName)

  useEffect(() => {
    if (!allUsers) {
      userApi.getList().then(({ users }) => {
        const nonAdminUsers = users.filter(user => !user.isInternalGlobalUser)
        setUsers(nonAdminUsers)
      })
    } else {
      setUsers(allUsers.filter(user => !user.isInternalGlobalUser))
    }
  }, [allUsers])

  const [users, setUsers] = useState()

  const { addToast } = useToasts()

  const userManageRole = useSelector(s => s.login.roles.UserManage)

  const isInternalAdmin = useInternalAdmin()

  function saveChanges() {
    userApi
      .updateInvoiceRecipients(users)
      .then(() => {
        addToast('Invoice recipients updated', { appearance: 'success' })
      })
      .catch(() => {
        addToast('Error updating invoice recipients', { appearance: 'error' })
      })
      .finally(() => {
        onSave()
      })
  }

  const canEditUsers =
    [userRoleLevels.HasPermission, userRoleLevels.HasGroupLevelPermission].includes(
      userManageRole
    ) || isInternalAdmin

  if (!canEditUsers) {
    return (
      <ConfirmationDialog
        title="You don't have permissions to manage users"
        onClose={onClose}
        positiveMessage="Close"
      >
        <p className={styles.permissionMissingText}>
          If you need this access,{' '}
          <button
            onClick={() => {
              window.$crisp.push(['do', 'chat:open'])
              window.$crisp.push([
                'set',
                'message:text',
                [`Hello, I need user manage permission for ${hotelName}`]
              ])
            }}
          >
            contact us.
          </button>
        </p>
      </ConfirmationDialog>
    )
  }

  if (!users) {
    return null
  }

  return (
    <ConfirmationDialog
      hasHeader={false}
      positiveMessage="Update preferences"
      negativeMessage="Cancel"
      theme="blacklist"
      contentPadding="0"
      maxWidth={400}
      onReject={onClose}
      onConfirm={saveChanges}
      isButtonDisabled={!users?.some(user => user.isInvoiceRecipient)}
    >
      <div className={styles.header}>
        <p className={styles.title}>Who shall receive invoices for {hotelName}?</p>
        <p className={styles.titleInfo}>
          <InfoIcon />
          Select at least one recipient
        </p>
      </div>
      <div className={styles.content}>
        <div className={styles.formHolder}>
          {users?.map(user => (
            <label className={styles.userLabel} key={user.userId}>
              <Toggle
                checked={user.isInvoiceRecipient}
                handleChange={e =>
                  setUsers(
                    users.map(u =>
                      u.userId === user.userId
                        ? { ...u, isInvoiceRecipient: e.target.checked }
                        : u
                    )
                  )
                }
              />

              <div className={styles.userInfo}>
                <p className={styles.userName}>{user.name}</p>
                <p className={styles.userEmail}>{user.email}</p>
              </div>
            </label>
          ))}
        </div>
        {onAddUserClick && (
          <button className={styles.addUserButton} onClick={onAddUserClick}>
            <AddIcon />
            Add a new user
          </button>
        )}
      </div>
    </ConfirmationDialog>
  )
}

export default InvoiceRecipientsDialog
